/**
 * The eight channel slots, and the rules that turn a stored binding into
 * something a person can read.
 *
 * The catalogue is fixed and lives here rather than coming from the service.
 * Which channels the platform supports is a platform decision, not an account's
 * data, and every account sees every slot whether it has a binding or not. A
 * screen that hid the empty ones would answer "what can I do here" with silence.
 */

/** The platform's channel slots, in the order they are always shown. */
export const CHANNELS = [
    {
        id: 'browser',
        bindingChannel: 'email',
        abbr: 'BR',
        name: 'Browser',
        capability: 'Browser survey · invitations emailed',
        metered: false,
        markColor: 'var(--color-categorical-4)',
        intake: 'fieldset',
        comingSoon: false,
        slotNote: 'Not provisioned. Defaulting to the SamSurveyor email server.',
    },
    {
        id: 'whatsapp',
        abbr: 'WA',
        name: 'WhatsApp',
        capability: 'Text, buttons, images · templated invite',
        metered: true,
        markColor: 'var(--color-categorical-1)',
        intake: 'fieldset',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs the WABA ID, WhatsApp-enabled number and approved invitation template.',
    },
    {
        id: 'messenger',
        abbr: 'FB',
        name: 'Facebook Messenger',
        capability: 'Text, buttons, cards · link-initiated',
        metered: false,
        markColor: 'var(--color-categorical-3)',
        intake: 'fieldset',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs a page authorization: page ID, page token, app secret.',
    },
    {
        id: 'sms',
        abbr: 'SM',
        name: 'SMS',
        capability: 'Text only · link or short token',
        metered: true,
        markColor: 'var(--color-categorical-7)',
        intake: 'fieldset',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs your numbers plus 10DLC brand and campaign IDs, or hosting authorization.',
    },
    {
        id: 'rcs',
        abbr: 'RC',
        name: 'RCS',
        capability: 'Rich cards, suggested replies',
        metered: false,
        markColor: 'var(--color-categorical-unpaletted)',
        intake: 'none',
        comingSoon: true,
        slotNote: 'Coming soon — messaging partner access is pending. Brand verification with Google is required per agent; nothing to enter yet. The slot stays visible so the roadmap is legible.',
    },
    {
        id: 'googlechat',
        abbr: 'GC',
        name: 'Google Chat',
        capability: 'Text, cards · bot DM or space',
        metered: false,
        markColor: 'var(--color-categorical-5)',
        intake: 'oauth',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs a Workspace admin to grant domain-wide delegation. No code, one admin-console act.',
    },
    {
        id: 'msteams',
        abbr: 'MT',
        name: 'Microsoft Teams',
        capability: 'Adaptive Cards · richest survey surface',
        metered: false,
        markColor: 'var(--color-categorical-3)',
        intake: 'oauth',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs your Azure AD tenant ID, admin consent, and the app installed from your org catalogue. Two steps, both in your own admin centre.',
    },
    {
        id: 'slack',
        abbr: 'SL',
        name: 'Slack',
        capability: 'Block Kit · bot DM',
        metered: false,
        markColor: 'var(--color-categorical-6)',
        intake: 'oauth',
        comingSoon: false,
        slotNote: 'Not provisioned. Needs an OAuth install into the workspace — press Connect and approve.',
    },
];

/** One catalogue entry, or undefined when the id is not one of the eight. */
export function channelById(id) {
    const canonical = id === 'email' || id === 'web' ? 'browser' : id;
    return CHANNELS.find((channel) => channel.id === canonical);
}

/**
 * The name to print for a channel.
 *
 * An unknown id prints itself. A row that named a channel this screen has never
 * heard of would otherwise have a blank where its name belongs, and nobody could
 * tell which row it was.
 */
export function channelName(id) {
    const match = channelById(id);
    return match ? match.name : String(id ?? '');
}

/** The compact identity represented by a binding in channel search/help. */
export function bindingIdentityLine(binding = {}) {
    const ids = binding.platform_identifiers || {};
    const parts = Object.keys(ids)
        .filter((key) => ids[key] !== null && ids[key] !== undefined && ids[key] !== '')
        .map((key) => `${key}: ${ids[key]}`);
    if (binding.webhook_route_key) parts.unshift(binding.webhook_route_key);
    return parts.length ? parts.join(' · ') : (binding.provider || '');
}

/**
 * Whether the one-row-per-channel status matrix should keep this whole row.
 *
 * A binding match keeps every binding in the row. Filtering the children would
 * silently change the displayed channel totals, which is never a valid search
 * result for a status dashboard.
 */
export function channelStatusRowMatches(channel, bindings = [], query = '') {
    const term = query.trim().toLowerCase();
    if (!term) return true;
    const count = bindings.length ? `${bindings.length} binding${bindings.length === 1 ? '' : 's'}` : '— bindings';
    const metering = channel.comingSoon ? 'coming soon' : (channel.metered ? 'metered rate schedule' : 'no per-message cost');
    const channelText = [channel.id, channel.abbr, channel.name, channel.capability, metering, count]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    if (channelText.includes(term)) return true;
    return bindings.some((binding) => [binding.display_label, bindingIdentityLine(binding)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term));
}

/**
 * What the button on a channel's group row says.
 *
 * Every channel says the same word, because the button does the same thing: it
 * opens that channel's own setup. It is not a generic form pointed at whatever
 * was clicked. What Email needs and what Slack needs have almost nothing in
 * common, so one shared wizard could only ever fit one of them.
 *
 * RCS has one too. Nothing can be configured there yet, and pressing it says so,
 * which is more use than a row with no way in and no explanation.
 */
export function bindingsButtonLabel() {
    return 'Bindings';
}

/**
 * How each lifecycle state is shown.
 *
 * Paused and disabled are neutral and never red. Red means something is wrong.
 * Somebody who deliberately paused a channel has not made a mistake, and a red
 * row would send them looking for a fault that is not there.
 */
export const STATUS_TAGS = {
    draft: { label: 'Draft', tone: 'yellow' },
    validated: { label: 'Validated', tone: 'yellow' },
    awaiting_platform: { label: 'Awaiting Platform', tone: 'yellow' },
    active: { label: 'Active', tone: 'green' },
    paused: { label: 'Paused', tone: 'neutral' },
    disabled: { label: 'Disabled', tone: 'neutral' },
    credential_expired: { label: 'Credential Expired', tone: 'red' },
};

/** The tag for a state, with an honest fallback for one this screen does not know. */
export function statusTag(state) {
    return STATUS_TAGS[state] || { label: state || 'Unknown', tone: 'neutral' };
}

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/**
 * Whether this binding's credential still works, in words.
 *
 * The two-week warning is the point of this function. A credential that expires
 * without warning stops every send on that channel, and the first anybody hears
 * of it is a customer asking why nothing arrived.
 *
 * `now` is a parameter so the behaviour can be tested without waiting.
 */
export function credentialHealth(binding, now = new Date()) {
    if (binding?.state === 'credential_expired') {
        return { text: 'Expired', tone: 'red' };
    }
    if (!binding?.credential_expires_at) {
        // Not every platform tells us when a credential dies. Saying so is more
        // useful than showing a green tick nobody checked.
        return { text: 'No expiry recorded', tone: 'neutral' };
    }

    const expiry = new Date(binding.credential_expires_at);
    const remaining = expiry.getTime() - now.getTime();

    if (remaining <= 0) return { text: 'Expired', tone: 'red' };

    if (remaining <= 14 * DAY_MS) {
        const days = Math.max(1, Math.ceil(remaining / DAY_MS));
        return { text: `Expires in ${days === 1 ? '1 day' : `${days} days`}`, tone: 'yellow' };
    }

    const when = expiry.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return { text: `Valid to ${when}`, tone: 'green' };
}

/**
 * Whether anything has arrived on this binding lately.
 *
 * A channel that has gone silent is the failure nothing else reports. Sending
 * keeps working, so no error appears anywhere, and the replies simply stop.
 *
 * Email is the exception and answers "not applicable". It carries invitations
 * and is never the conversation, so it has no inbound lane to be silent.
 */
export function inboundHealth(binding, now = new Date()) {
    if (binding?.channel === 'email') {
        return { text: 'n/a', note: 'outbound only', stale: false };
    }
    if (!binding?.last_inbound_at) {
        return { text: 'No inbound yet', note: '', stale: false };
    }

    const since = Math.max(0, now.getTime() - new Date(binding.last_inbound_at).getTime());

    if (since < HOUR_MS) {
        const minutes = Math.max(1, Math.floor(since / 60000));
        return { text: `Inbound ${minutes} min ago`, note: 'webhook healthy', stale: false };
    }
    if (since < 48 * HOUR_MS) {
        return { text: `Inbound ${Math.floor(since / HOUR_MS)} h ago`, note: 'webhook healthy', stale: false };
    }
    return {
        text: `No inbound ${Math.floor(since / DAY_MS)} days`,
        note: 'check platform config',
        stale: true,
    };
}

/**
 * The "Needs attention" rows, worked out from the bindings themselves.
 *
 * The order is severity, not time. A halted channel comes before a review that
 * needs nothing from anybody. Sorting by when each happened would put a
 * five-minute-old "nothing to do" above a two-day outage.
 *
 * Nothing wrong returns an empty list, and the panel then says so in words.
 */
export function attentionItems(bindings = [], now = new Date()) {
    const list = Array.isArray(bindings) ? bindings : [];
    const items = [];

    for (const b of list) {
        if (b.state === 'credential_expired') {
            items.push({
                key: `${b.binding_id}:credential_expired`,
                tone: 'red',
                title: `${channelName(b.channel)} credential expired`,
                detail: `${b.display_label} · sends halted until it is replaced.`,
                action: 'Re-authorize',
            });
        }
    }

    for (const b of list) {
        if (b.state !== 'active') continue;
        if (!inboundHealth(b, now).stale) continue;
        const days = Math.floor((now.getTime() - new Date(b.last_inbound_at).getTime()) / DAY_MS);
        items.push({
            key: `${b.binding_id}:inbound_stale`,
            tone: 'yellow',
            title: `${channelName(b.channel)} has heard nothing for ${days} days`,
            detail: `${b.display_label} · last inbound ${days} days ago. Platform-side config may have changed.`,
            action: 'Diagnose',
        });
    }

    for (const b of list) {
        if (b.state === 'awaiting_platform') {
            items.push({
                key: `${b.binding_id}:awaiting_platform`,
                tone: 'yellow',
                title: `${channelName(b.channel)} review running`,
                detail: `${b.display_label} · nothing to do — we activate the binding on approval.`,
                action: 'View',
            });
        }
    }

    for (const b of list) {
        if (b.state !== 'active') continue;
        const health = credentialHealth(b, now);
        if (health.tone !== 'yellow') continue;
        items.push({
            key: `${b.binding_id}:credential_expiring`,
            tone: 'yellow',
            title: `${channelName(b.channel)} credential expires soon`,
            detail: `${b.display_label} · ${health.text}.`,
            action: 'Re-authorize',
        });
    }

    return items;
}
